import { Router } from '@angular/router';
import { environment } from './../../../environments/environment';
import { AppActions } from './../store/app.reducer';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IState } from '../store/root.reducer';
import { CurrentUserActions } from '../store/current-user.reducer';
import { map, share } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { AppSettingsService } from './app-settings.service';
import * as jwt_decode from 'jwt-decode';
import { CommonApiService } from './common-api.service';
import { IUserPendoInfo } from '../domain/user-pendoInfo.entity';
import { stopRumSession } from 'ops-datadog-rum-core';

const tokenEndPoint = 'oauth2/token';
const meEndpoint = 'me/v2';
const redirectEndpoint = 'redirect';
const recoverEndpoint = 'redirect/recover';
const externalLogin = 'external-login?guid=';

const refreshTokenKey = 'ops.id.refreshToken';
const accessTokenKey = 'ops.id.accessToken';
const expiresTokenKey = 'ops.id.accessTokenExpiration';
const currentUserKey = 'ops.id.currentUser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private store: Store<IState>,
    private settings: AppSettingsService,
    private router: Router,
    private commonApiService: CommonApiService,
  ) {}

  relogin(): Observable<string> {
    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;
    const body: HttpParams = new HttpParams()
      .set('grant_type', 'relogin')
      .set('client_Id', environment.clientId)
      .set('accessToken', this.getAccessToken())
      .set('environment', environment.environment);

    return this.http.post(url, body).pipe(map(res => this.mapTokenResponse(JSON.parse(res.toString()))));
  }

  refreshToken(): Observable<string> {
    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;
    const refreshToken = this.localStorage.get(refreshTokenKey);
    let body: HttpParams;
    if (refreshToken) {
      body = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('client_Id', environment.clientId)
        .set('refresh_token', refreshToken.toString());
    } else {
      return EMPTY;
    }
    return this.http.post<any>(url, body, this.getOptions())
      .pipe(map(res => {
        if (!res.access_token) {
          return null;
        }

        const accessToken = this.mapTokenResponse(res);
        return accessToken;
      })).pipe(share());
  }

  requestAccessToken(cfid: string): Observable<string> {
    if (!cfid) {
      throw new Error(`cfid can not be empty`);
    }

    const url = `${environment.identityApiBaseUrl}${tokenEndPoint}`;

    const body = new HttpParams()
      .set('grant_type', 'coldfusionsessionid')
      .set('client_Id', environment.clientId)
      .set('cfid', cfid);

    return this.http.post<any>(url, body, this.getOptions())
      .pipe(map(res => {
        if (!res.access_token) {
          return null;
        }

        const accessToken = this.mapTokenResponse(res);
        // this.relogin();
        return accessToken;
      }));
  }

  login(cfid: string) {
    this.localStorage.clearAll();
    this.requestAccessToken(cfid).subscribe(() => {
      this.mapTokenToUser(cfid);
      this.settings.reloadSettings();
      this.reloadData();
      this.router.navigate(['user-redirect']);
    });
  }

  private mapTokenToUser(cfid: string) {
    const decodedToken = jwt_decode(this.getAccessToken());
    const currentUser: User = {
      id: +decodedToken.ContactId,
      grantType: decodedToken.GrantType,
      features: decodedToken.Features,
      featuresCodes: decodedToken.role,
      companyId: +decodedToken.CompanyId,
      defaultContact: 0,
      contactId: +decodedToken.ContactId,
      fullName: decodedToken.FullName,
      timeZone: undefined,
      userName: decodedToken.unique_name,
      userType: +decodedToken.UserType,
      emailAddresses: [],
      shippingVendorId: +decodedToken.ShippingVendorId,
      manufacturers: decodedToken,
      showTutorial: undefined,
      shopId: undefined,
      firstName: decodedToken.FirstName,
      lastName: decodedToken.LastName,
      companyName: decodedToken.CompanyName,
      userGuid: undefined,
      marketId: 0,
      cfid: decodedToken.Cfid ? decodedToken.Cfid : cfid,
      oecCompanyId: decodedToken?.OecCompanyId,
      oecUserId: decodedToken?.OecUserId,
      opsUserId: decodedToken.OpsUserId,
      msoType: decodedToken.MsoType,
      isProxyLogin: decodedToken.isProxyLogin
    };

    this.localStorage.set(currentUserKey, currentUser);
    this.store.dispatch({
      type: CurrentUserActions.SET_USER,
      payload: currentUser
    });
    this.store.dispatch({
      type: AppActions.SET_APP_ID,
      payload: currentUser.userType === 1 ? environment.appIdShop : environment.appIdDealer
    });
  }

  getAccessToken(): string {
    const token = this.localStorage.get(accessTokenKey);
    return token ? token.toString() : '';
  }

  isAuthenticated(): boolean {
    return this.localStorage.get(accessTokenKey) ? true : false;
  }

  getCurrentUser(): User {
    return this.localStorage.get(currentUserKey) as User;
  }

  requestCurrentUser(cfid: string): Observable<any> {
    const url = `${environment.identityApiBaseUrl}${meEndpoint}`;
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${this.getAccessToken()}`
      })
    };

    return this.http.get<any>(url, options);
  }

  private reloadData() {
    this.store.pipe(select((state: IState) => state.currentUser))
      .subscribe(user => {
        this.store.dispatch({
          type: AppActions.RELOAD_DATA,
          payload: true
        });
      });
  }

  private getOptions() {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/x-www-form-urlencoded')
    };
    return httpOptions;
  }

  private mapTokenResponse(res: any) {
    if (res.userName && res.access_token) {
      this.localStorage.set(accessTokenKey, res.access_token);
      this.localStorage.set(expiresTokenKey, res['.expires']);
      this.localStorage.set(refreshTokenKey, res.refresh_token);
      return res.access_token;
    } else {
      return null;
    }
  }

  logout() {
    this.clearUserFromLocalStorage();
    stopRumSession();
    this.commonApiService.getKeycloakEnabled().subscribe((resp) => {
      if (resp.enabled) {
        window.location.href = `${environment.oecLogoutUrl}?redirect_uri=${environment.baseUrl}`;
      } else {
        window.location.href = `${environment.logoutRedirectUrl}`;
      }
    });
  }

  clearUserFromLocalStorage() {
    this.localStorage.remove(accessTokenKey, expiresTokenKey, refreshTokenKey, currentUserKey);
  }

  loadUserFromLocalStore() {
    const currentUser = this.localStorage.get(currentUserKey);
    if (currentUser) {
      this.store.dispatch({
        type: CurrentUserActions.SET_USER,
        payload: currentUser
      });
      this.store.dispatch({
        type: AppActions.SET_APP_ID,
        payload: (currentUser as User).userType === 1 ? environment.appIdShop : environment.appIdDealer
      });
    }
  }

  reloadSettings() {
    this.settings.reloadSettings();
  }

  redirect(targetUrl: string, parameters: string, targetAppUrl: string): Observable<string> {
    const currentUser = this.localStorage.get(currentUserKey);

    const requestData: RedirectData = {
      accessToken: this.localStorage.get(accessTokenKey).toString(),
      expiresToken: this.localStorage.get(expiresTokenKey).toString(),
      refreshToken: this.localStorage.get(refreshTokenKey).toString(),
      currentUser: JSON.stringify(currentUser),
      url: targetUrl,
      parameters: parameters
    };

    const options = {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.getAccessToken()}`
      })
    };
    const url = `${environment.identityApiBaseUrl}${redirectEndpoint}`;
    // UNCOMENT THIS LINE FOR LOCAL TESTING
    // const url = `http://localhost/api/id/${recoverEndpoint}`;

    return this.http.post(url, requestData, options)
      .pipe(map(res => {
        return `${targetAppUrl}${externalLogin}${res}`;
      }));
  }

  loginExternal(guid): Observable<RedirectDataC> {
    const requestData = { guid: guid };
    const options = { headers: new HttpHeaders({}) };
    const url = `${environment.identityApiBaseUrl}${recoverEndpoint}`;

    return this.http.post(url, requestData, options)
      .pipe(map(res => {
        if (res) {
          const result: RedirectDataC = res as RedirectDataC;
          if (result) {
            this.localStorage.set(accessTokenKey, result.AccessToken);
            this.localStorage.set(expiresTokenKey, result.ExpiresToken);
            this.localStorage.set(refreshTokenKey, result.RefreshToken);
            const currentUser = JSON.parse(result.CurrentUser);
            this.localStorage.set(currentUserKey, currentUser);
            this.store.dispatch({
              type: CurrentUserActions.SET_USER,
              payload: currentUser
            });
            this.store.dispatch({
              type: AppActions.SET_APP_ID,
              payload: currentUser.userType === 1 ? environment.appIdShop : environment.appIdDealer
            });
            this.store.dispatch({
              type: AppActions.RELOAD_DATA,
              payload: true
            });

            result.userType = currentUser.userType;
            return result;
          } else {
            window.location.href = (`${environment.logoutRedirectUrl}`);
          }
        } else {
          window.location.href = (`${environment.logoutRedirectUrl}`);
        }
      }));
  }

  anonymousUser(companyName: string) {
    const currentUser: User = {
      id: -1,
      grantType: '',
      features: [''],
      featuresCodes: [''],
      companyId: -1,
      defaultContact: 0,
      contactId: -1,
      fullName: 'Testing Full Name',
      timeZone: undefined,
      userName: '',
      userType: 0,
      emailAddresses: [],
      shippingVendorId: 0,
      manufacturers: [''],
      showTutorial: undefined,
      shopId: undefined,
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: companyName,
      userGuid: undefined,
      marketId: 0,
      cfid: '',
      isAnon: true,
      isOEMSupplier: false,
      opsUserId: undefined,
      msoType: '',
      isProxyLogin: false
    };

    this.localStorage.set(currentUserKey, currentUser);
    this.store.dispatch({
      type: CurrentUserActions.SET_USER,
      payload: currentUser
    });
  }
}
export interface RequestBody {
  grant_type: string;
  client_Id: string;
  cfid: string;
}

export interface User {
  id: number;
  grantType: string;
  features: string[];
  featuresCodes: string[];
  companyId: number;
  defaultContact: number;
  contactId: number;
  fullName: string;
  timeZone: string;
  userName: string;
  userType: number;
  emailAddresses: string[];
  shippingVendorId: number;
  manufacturers: string[];
  showTutorial: number;
  shopId: number;
  firstName: string;
  lastName: string;
  companyName: string;
  userGuid: string;
  marketId: number;
  cfid?: string;
  isAnon?: boolean;
  oecCompanyId?: string;
  oecUserId?: string;
  opsUserId: number;
  isOEMSupplier?: boolean;
  isTraxPartnersPendoEnabled?: boolean;
  pendoApiKey?: string;
  pendo?: IUserPendoInfo;
  msoType: string;
  isProxyLogin: boolean;
}

interface RedirectData {
  accessToken: string;
  expiresToken: string;
  refreshToken: string;
  currentUser: string;
  url: string;
  parameters: string;
}

interface RedirectDataC {
  AccessToken: string;
  ExpiresToken: string;
  RefreshToken: string;
  CurrentUser: string;
  Url: string;
  Parameters: string;
  userType?: number;
}
